import { ref, reactive, computed, watch } from 'vue';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import Utils from '@/api/Utils';
import { useBaseStationsStore } from '@/stores/baseStations';
import { useBaselinesStore } from '@/stores/baselinesStore';

import { showErrorMessage } from '@/components/common/errorMessage';

import { addBaselineStatusData } from '@/components/addBaseline/baselineUtils';

export function useBaselineData(baselineId) {
	const baselinesStore = useBaselinesStore();
	const selectedGnssOptions = computed(() => {
		const options = baselinesStore.settings?.selected_gnss_options || [];
		return Consts.sortGnssCodes(options);
	});

	// создает объекты с данными БС
	// возвращает функцию для загрузки данных БС
	const rawSelectedGnss = ref(baselinesStore.settings?.selected_gnss_default || []);
	const formState = reactive({
		base: '',
		rover: '',
		nmeaPortExt: '',
		description: '',
		selectedGnss: rawSelectedGnss.value,
	});

	const baseStationsStore = useBaseStationsStore();
	const bsOptions = computed(() => Utils.sortArray(
		baseStationsStore.items.filter(item => item.status === 'working' && !item.deleted).map(item => ({ label: item.mntpnt_int, value: item.id })),
		Utils.objectFieldMultiSorter('label'),
	));
	// данные из store
	const baselineObj = computed(() => {
		let baselines = baselinesStore.items.filter(item => item.id === baselineId.value);
		return (baselines.length > 0) ? addBaselineStatusData(baselines[0]) : null;
	});

	// допданные из запроса при редактировании
	const baselineDetails = ref({});
	const isLoading = ref(false);

	const updateSelectedGnss = () => {
		formState.selectedGnss = rawSelectedGnss.value.filter(code => selectedGnssOptions.value.includes(code));
	}

	watch(selectedGnssOptions, (to, from) => {
		if (from == null && to != null) {
			if (baselineId.value === 0) {
				rawSelectedGnss.value = baselinesStore.settings.selected_gnss_default;
			}
			updateSelectedGnss();
		}
	}, { immediate: true });

	const loadBaselineDetails = () => {
		isLoading.value = true;
		Api.baselineGetDetails(baselineId.value, response => {
			if (response.status === 'ok') {
				const data = Api.deserializeBaselineDetails(response.baseline);
				baselineDetails.value = data;
				formState.base = data.base.mntpnt_int;
				formState.rover = data.rover.mntpnt_int;
				formState.nmeaPortExt = data.nmeaPortExt;
				formState.description = data.description;
				rawSelectedGnss.value = data.selectedGnss;
				updateSelectedGnss();
			} else {
				showErrorMessage(response.errors);
			}
			isLoading.value = false;
		});
	}

	const isDeleted = computed(() => baselineObj.value === null && baselineDetails.value.deleted);
	const nmeaFtpPath = computed(() => {
		if (baselineId.value === 0 || formState.nmeaPortExt === '' || formState.nmeaPortExt === null) {
			return '';
		}
		return `ftpout/baselines/id${baselineId.value}_port${formState.nmeaPortExt}_%Y-%m-%d-%H-00.nmea`;
	});
	const isBaselineActive = computed(() => {
		return (
			baselineObj.value !== null
			&& !['no_data', 'offline', 'broken', 'stopped'].includes(baselineObj.value.statusData.key)
			&& !baselineObj.value.deleted
		);
	});
	return {
		formState,
		baselineObj,
		baselineDetails,
		selectedGnssOptions,
		bsOptions,
		isLoading,
		loadBaselineDetails,
		isDeleted,
		nmeaFtpPath,
		isBaselineActive,
	}
}
